<template>
  <panel title="Lista wypłat" class="payment-list">
    <div slot="toolbar" class="form-group payment-list-toolbar">
      <div v-if="!splitDisabled" class="d-inline-block mr-5 mb-2">
        <w-input
          class="d-inline-block mr-2"
          :model="$schema.split.grant"
          :disabled="!splitEditable"
          @input="calculateContribution"
        />

        <w-input
          class="d-inline-block mr-2"
          :model="$schema.split.contribution"
          :disabled="!splitEditable"
          @input="calculateGrant"
        />

        <w-button v-if="!splitEditable" class="btn-primary mt-4" text="Zmień rozkład źródeł" @click="unlockSplit" />

        <w-button v-else class="btn-primary mt-4" text="Wylicz automatycznie" @click="lockSplit" />
      </div>

      <div class="d-inline-block ml-5 mb-2">
        <w-select
          v-if="availableSchools.length"
          class="d-inline-block mr-2"
          style="width: 400px"
          :model="$schema.filters.selectedSchool"
          :values="availableSchools"
        />

        <w-select
          v-if="types && types.length"
          class="d-inline-block mr-2"
          style="width: 400px"
          :model="$schema.filters.selectedType"
          :values="types"
        />

        <w-select class="d-inline-block mr-2" :model="$schema.filters.year" :values="years" />

        <w-select class="d-inline-block month-select mr-2" :model="$schema.filters.month" :values="months" />

        <w-button class="btn-primary mb-2 mt-4" text="Pokaż" @click="reload" />
      </div>
    </div>

    <w-table
      :columns="columns"
      :data="paymentList"
      :show-checkboxes="true"
      :hide-add-button="true"
      :hide-search-input="true"
      :per-page="-1"
      :total="1"
      @checkAll="checkAll"
      @checked="updateSplit"
    />

    <div slot="footer">
      <w-button class="btn-secondary mt-4" text="Wróć" @click="goBack" />

      <div v-if="!ksatDisabled" class="float-right">
        <payment-pack-picker class="ml-2" :model="$schema.submit.packId" :procedure-id="procedureId" />

        <w-button
          class="btn-primary ml-2 mt-4"
          text="Prześlij do KSAT"
          :disabled="!checkedIds.length"
          @click="confirmSendToKsat"
        />
      </div>

      <div v-else class="float-right">
        <w-button
          class="btn-primary ml-2 mt-4"
          text="Prześlij do MDOK"
          :disabled="!checkedIds.length"
          @click="confirmSendToMdok"
        />
      </div>

      <w-datepicker class="float-right" :model="$schema.submit.paymentDate" />
    </div>
  </panel>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';

import PaymentPackPicker from '@/components/formComponents/PaymentPackPicker';
import WTable from '@/components/table/Table';
import Panel from '@/components/panel/Panel';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Successes } from '@/enums/successes.enum';
import { formMixin } from '@/mixins/form';
import { notificationsMixin } from '@/mixins/notifications';
import schema from '@/schemas/paymentList.schema';
import store from '@/store/store';
import { config } from '@/utils/config';
import { getYears, months } from '@/utils/dates';
import { tryParseInt } from '@/utils/number';
import { getPaymentList, getPaymentListDate, getPaymentListVariables } from '@/utils/paymentList';
import { serverError } from '@/utils/serverError';

// @vuese
// Lista wypłat
// @group Procedure
export default {
  components: { Panel, PaymentPackPicker, WTable },
  mixins: [formMixin, notificationsMixin],
  schema,

  data() {
    const month = tryParseInt(this.$route.query.month);
    const split = store.state.procedure.paymentListSplit;
    return {
      model: {
        split: split
          ? {
              contribution: split.contribution * 100,
              grant: split.grant * 100
            }
          : {},
        filters: {
          month: month !== undefined ? month : new Date().getMonth(),
          year: tryParseInt(this.$route.query.year) || new Date().getFullYear(),
          selectedSchool: undefined
        },
        submit: { paymentDate: moment().format('YYYY-MM-DD') }
      },
      years: getYears(),
      splitEditable: false
    };
  },

  computed: {
    ...mapState({
      paymentList(state) {
        return state.procedure.paymentList.map(row => {
          const grant = Math.round(row.amount * this.$schema.split.grant.$model) / 100;
          const contribution = row.amount - grant;
          row.grant = grant.toFixed(2);
          row.contribution = contribution.toFixed(2);
          return row;
        });
      },

      split: state => state.procedure.paymentListSplit,

      splitDisabled: state => state.setting.settings[PublicSettings.PAYMENT_NO_SPLIT]?.value === 'true',

      ksatDisabled: state => state.setting.settings[PublicSettings.KSAT_DISABLED]?.value === 'true',

      columns: state =>
        state.setting.settings[PublicSettings.PAYMENT_COLUMNS]?.value &&
        JSON.parse(state.setting.settings[PublicSettings.PAYMENT_COLUMNS].value),

      types: state =>
        state.setting.settings[PublicSettings.PAYMENT_LIST_TYPES]?.value &&
        JSON.parse(state.setting.settings[PublicSettings.PAYMENT_LIST_TYPES].value).map((type, index) => ({
          ...type,
          value: index
        })),

      procedureId(state) {
        return tryParseInt(
          state.setting.settings[PublicSettings.CHILD_PROCEDURE_ID]?.value,
          this.$route.params.procedureId
        );
      }
    }),
    ...mapGetters({ structures: 'auth/paymentListStructures' }),
    availableSchools() {
      return this.structures.map(school => ({ text: school.name, value: school.id }));
    },
    months() {
      return months(this.model.filters.year);
    },
    checkedIds() {
      return this.paymentList.filter(row => !!row.checked).map(row => row.documentId);
    }
  },

  mounted() {
    this.$schema.filters.selectedSchool.$setData(this.$route.query.selectedSchool || this.availableSchools?.[0]?.value);
    if (this.types?.length) {
      this.$schema.filters.selectedType.$setData(tryParseInt(this.$route.query.selectedType) || 0);
    }
  },

  methods: {
    // @vuese
    // Wraca do szczegółów procedury
    goBack() {
      this.$router.push({ name: 'procedureDetails' });
    },

    // @vuese
    // Pokazuje listę wypłat na wybraną datę
    reload() {
      const { year, month, selectedSchool, selectedType } = this.$schema.filters.$getData();
      this.$router.push({ name: this.$route.name, query: { month, year, selectedSchool, selectedType } });
    },

    // @vuese
    // Potwierdza przesłanie do KSAT
    confirmSendToKsat() {
      if (this.$schema.submit.$validateAll().length) {
        return this.notifyError(Errors.FORM_INCORRECTLY_COMPLETED);
      }

      this.$store.dispatch('setConfirm', {
        closeButton: 'Nie',
        submitButton: 'Tak',
        title: 'Czy na pewno chcesz przesłać zaznaczone pozycje do KSAT?',
        onSuccess: () => this.sendToKsat()
      });
    },

    // @vuese
    // Przesyła listę do KSAT
    sendToKsat() {
      if (this.$schema.submit.$validateAll().length) {
        return this.notifyError(Errors.FORM_INCORRECTLY_COMPLETED);
      }
      const data = getPaymentListVariables(this.$route, this.procedureId);
      if (this.splitEditable) {
        const { grant, contribution } = this.$schema.$getData();
        data.grant = grant / 100;
        data.contribution = contribution / 100;
      }
      data.documentIds = this.checkedIds;
      const { paymentDate, packId } = this.$schema.submit.$getData();
      data.paymentDate = paymentDate;
      data.packId = packId;
      this.$store
        .dispatch('procedure/paymentListSendToKsat', data)
        .then(result => {
          const description = result.length ? result.join('<br>') + '<br>' + Errors.CONTACT_WITH_KSAT_ADMIN : undefined;
          this.$store.dispatch('setConfirm', {
            description,
            showCloseButton: false,
            submitButton: 'Ok',
            title: Successes.PAYMENT_LIST_SAVED,
            onSuccess: () => getPaymentList(this.$route)
          });
        })
        .catch(err => {
          this.$store.dispatch('setConfirm', {
            description: serverError(err) + '<br>' + Errors.CONTACT_WITH_KSAT_ADMIN,
            showCloseButton: false,
            submitButton: 'Ok',
            title: Errors.PAYMENT_LIST_CANNOT_SAVE
          });
        });
    },

    // @vuese
    // Potwierdza przesłanie do MDOK
    confirmSendToMdok() {
      if (this.$schema.submit.$validateAll().length) {
        return this.notifyError(Errors.FORM_INCORRECTLY_COMPLETED);
      }

      this.$store.dispatch('setConfirm', {
        closeButton: 'Nie',
        submitButton: 'Tak',
        title: 'Czy na pewno chcesz przesłać zaznaczone pozycje do MDOK?',
        onSuccess: () => this.sendToMdok()
      });
    },

    // @vuese
    // Przesyła listę do MODK
    sendToMdok() {
      if (this.$schema.submit.$validateAll().length) {
        return this.notifyError(Errors.FORM_INCORRECTLY_COMPLETED);
      }
      const { paymentDate } = this.$schema.submit.$getData();
      const data = getPaymentListVariables(this.$route, this.procedureId);
      data.documentIds = this.checkedIds;
      data.paymentDate = paymentDate;
      this.$store
        .dispatch('procedure/paymentListSendToMdok', data)
        .then(result => {
          this.$store.dispatch('setConfirm', {
            showCloseButton: false,
            submitButton: 'Ok',
            title: Successes.PAYMENT_LIST_SAVED,
            onSuccess: () => getPaymentList(this.$route)
          });
        })
        .catch(err => {
          this.$store.dispatch('setConfirm', {
            description: serverError(err),
            showCloseButton: false,
            submitButton: 'Ok',
            title: Errors.PAYMENT_LIST_CANNOT_SAVE
          });
        });
    },

    // @vuese
    // Zaznacza lub odznacza wszystko
    checkAll(checked) {
      this.paymentList.forEach(row => (row.checked = checked));
    },

    // @vuese
    // Odblokowuje edycję rozkładu źródeł wypłat
    unlockSplit() {
      this.$store.dispatch('setConfirm', {
        title: 'Czy na pewno chcesz zmienić rozkład źródeł wypłat?',
        description: 'Zmiana rozkładu źródeł wypłat może spowodować nieprawidłowe rozliczenie wypłat.',
        onSuccess: () => (this.splitEditable = true)
      });
    },

    // @vuese
    // Blokuje edycję rozkładu źródeł i wczytuje domyślne, pobrane z API
    lockSplit() {
      this.splitEditable = false;
      this.$schema.grant.$setData(this.split.grant * 100);
      this.$schema.contribution.$setData(this.split.contribution * 100);
    },

    // @vuese
    // Przelicza kwotę wkładu własnego
    calculateContribution() {
      this.calculateSplit('grant', 'contribution');
    },

    // @vuese
    // Przelicza kwotę dotacji
    calculateGrant() {
      this.calculateSplit('contribution', 'grant');
    },

    // @vuese
    // Przelicza kwotę źródła
    calculateSplit(changedFieldName, fieldToChange) {
      const changedField = this.$schema.split[changedFieldName];
      if (changedField.$model > 100) {
        changedField.$setData(100);
      }
      this.$schema.split[fieldToChange].$setData(100 - changedField.$model);
    },

    // @vuese
    // Pobiera nowy podział źródeł
    updateSplit(ee) {
      if (this.splitEditable || this.split.contribution === 1 || this.split.grant === 0.8) {
        return;
      }

      const data = getPaymentListVariables(this.$route, this.procedureId);
      data.documentIds = this.paymentList.filter(row => !!row.checked).map(row => row.documentId);
      this.$store
        .dispatch('procedure/paymentListSplit', data)
        .then(split => {
          this.$schema.split.grant.$setData(split.grant * 100);
          this.$schema.split.contribution.$setData(split.contribution * 100);
        })
        .catch(err => this.notifyError(Errors.PAYMENT_LIST_CANNOT_GET));
    }
  },

  beforeRouteUpdate(to, from, next) {
    getPaymentList(to).then(() => next());
  }
};
</script>

<style>
.payment-list-toolbar .d-inline-block {
  vertical-align: top;
}

.payment-list .payment-pack-picker {
  min-width: 300px;
}

.payment-list .month-select {
  width: 120px;
}
</style>
